import type * as React from "react";
import type { ContentItem, ContentType, Program, ProgramColor } from "./types";

/**
 * Replaces the browser's default (opaque) drag image with a translucent copy
 * of the dragged element, so the drop-slot preview TimelineGrid/WeekGrid draw
 * underneath the cursor stays visible instead of being hidden behind a solid
 * card while dragging.
 */
export function setTranslucentDragImage(e: React.DragEvent, source: HTMLElement): void {
  if (typeof e.dataTransfer?.setDragImage !== "function") return;
  const rect = source.getBoundingClientRect();
  const clone = source.cloneNode(true) as HTMLElement;
  clone.style.width = `${rect.width}px`;
  clone.style.height = `${rect.height}px`;
  clone.style.opacity = "0.5";
  clone.style.position = "fixed";
  clone.style.top = "-9999px";
  clone.style.left = "-9999px";
  clone.style.pointerEvents = "none";
  document.body.appendChild(clone);
  e.dataTransfer.setDragImage(clone, e.clientX - rect.left, e.clientY - rect.top);
  // The browser reads the drag image synchronously off setDragImage; the
  // clone only needs to survive that read.
  window.setTimeout(() => clone.remove(), 0);
}

/** "HH:mm" -> minutes since midnight ("24:00" is 1440). */
export function toMinutes(hhmm: string): number {
  const [hours, minutes] = hhmm.split(":").map(Number);
  return hours * 60 + minutes;
}

/** "YYYY-MM-DD" for a Date's local day. */
export function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** "HH:mm" (24h) -> "h:mm AM/PM". "24:00" reads as 12:00 AM, and hours past
 * 24 (a cross-midnight end like "25:30") wrap onto the next day's clock. */
export function formatHHmm(hhmm: string): string {
  const [hours, minutes] = hhmm.split(":").map(Number);
  const period = hours % 24 >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, "0")} ${period}`;
}

/** " +1" when an end time lands past midnight on the next day, else "".
 * "24:00" itself is the day's own end, not the next day's clock. */
export function nextDaySuffix(hhmm: string): string {
  return toMinutes(hhmm) > 24 * 60 ? " +1" : "";
}

export const DAY_MINUTES = 24 * 60;
/** A block starts within its day but may run past midnight into the next
 * one (11:30 PM - 1:30 AM): endTime minutes go up to 2880, one midnight
 * at most, mirroring the backend's bound. */
export const MAX_END_MINUTES = 2 * DAY_MINUTES;
/** The day timeline is a continuous strip of this many days from the
 * selected date, so scheduling scrolls right across midnights into the
 * future instead of stopping at the day edge. */
export const STRIP_DAYS = 7;
/** Days rendered before the selected date: one, so a block that started
 * yesterday evening and runs past midnight is visible (and editable) in
 * full by scrolling left, instead of appearing cut off at the strip edge. */
export const STRIP_PAST_DAYS = 1;

/** The strip's date keys for a selected date: STRIP_PAST_DAYS back through
 * STRIP_DAYS forward, in order. */
export function stripDateKeys(date: string): string[] {
  return Array.from({ length: STRIP_PAST_DAYS + STRIP_DAYS }, (_, i) =>
    shiftDateKey(date, i - STRIP_PAST_DAYS),
  );
}
// Drops land on exact minutes (an admin can place a block at 2:01), so the
// snap step is one minute; it exists only to kill sub-minute pixel noise.
export const SNAP_MINUTES = 1;
/** Fixed hour width: tighter than the user guide's 160 so most of the day
 * fits at once, still spread enough to read; the grid scrolls
 * horizontally across the whole multi-day strip. */
export const DEFAULT_PIXELS_PER_HOUR = 80;
/** The strip opens at the selected day's midnight; the future is a scroll away. */
export const VISIBLE_START_HOUR = 0;

/** The guide-matched fixed scale (width kept as a parameter so callers and
 * tests need no change if fitting ever returns). */
export function pixelsPerHourFor(_trackWidth: number): number {
  return DEFAULT_PIXELS_PER_HOUR;
}

/** Timeline zoom steps (px per hour), from a whole day on screen up to 96 px
 * per minute, where a 30 second upload is about 48 px wide. The default
 * (80) is the scale the grid has always opened at. */
export const ZOOM_LEVELS = [40, 80, 160, 320, 640, 1440, 2880, 5760];
export const DEFAULT_ZOOM_INDEX = ZOOM_LEVELS.indexOf(DEFAULT_PIXELS_PER_HOUR);

/** Minutes between labelled ticks for a zoom: coarse when zoomed out, down
 * to every minute when a single minute is wide enough to read. */
export function tickMinutesFor(pph: number): number {
  if (pph < 48) return 120;
  if (pph < 160) return 60;
  if (pph < 320) return 30;
  if (pph < 640) return 15;
  if (pph < 2880) return 5;
  return 1;
}

/** "1 AM" on the hour, "1:05" between hours (the AM/PM is on the hour tick
 * just before it). `minutes` is minutes into the day. */
export function tickLabel(minutes: number): string {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${h < 12 ? "AM" : "PM"}` : `${h12}:${String(m).padStart(2, "0")}`;
}

export const CONTENT_TYPE_LABEL: Record<ContentType, string> = {
  game: "Game",
  show: "Show",
  highlight: "Highlight",
  feature: "Feature",
  playlist: "Playlist",
  upload: "Upload",
  whizzard: "Whizzard",
};

export const TYPE_COLOR: Record<ContentType, ProgramColor> = {
  game: "blue",
  show: "green",
  highlight: "purple",
  feature: "pink",
  playlist: "yellow",
  upload: "green",
  whizzard: "purple",
};

/** Minutes since midnight -> "HH:mm" ("24:00" for end of day). */
export function toHHmm(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function snapMinutes(minutes: number, step = SNAP_MINUTES): number {
  return Math.round(minutes / step) * step;
}

/** Seconds -> "h:mm:ss". */
export function formatTimecode(totalSec: number): string {
  const sec = Math.max(0, Math.round(totalSec));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** "h:mm:ss", "hh:mm:ss" or "mm:ss" -> seconds, or null when malformed. */
export function parseTimecode(text: string): number | null {
  const parts = text.trim().split(":");
  if (parts.length < 2 || parts.length > 3) return null;
  if (!parts.every((p) => /^\d{1,2}$/.test(p))) return null;
  const nums = parts.map(Number);
  const [h, m, s] = nums.length === 3 ? nums : [0, nums[0], nums[1]];
  if (m > 59 || s > 59) return null;
  return h * 3600 + m * 60 + s;
}

export function dateFromKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function shiftDateKey(key: string, days: number): string {
  const date = dateFromKey(key);
  date.setDate(date.getDate() + days);
  return toDateKey(date);
}

/** Whole days from `fromKey` to `key` (positive when `key` is later).
 * Pure calendar math via UTC, so a DST change never yields a fraction. */
export function dayDiff(key: string, fromKey: string): number {
  const [y1, m1, d1] = key.split("-").map(Number);
  const [y2, m2, d2] = fromKey.split("-").map(Number);
  return Math.round((Date.UTC(y1, m1 - 1, d1) - Date.UTC(y2, m2 - 1, d2)) / 86_400_000);
}

/** A program's span in minutes on the axis whose zero is `originKey`'s
 * midnight; a block on a later day sits 1440 further right per day. This
 * is how cross-midnight blocks and neighbouring days compare directly. */
export function absSpan(p: Program, originKey: string): { start: number; end: number } {
  const offset = dayDiff(p.date, originKey) * DAY_MINUTES;
  return { start: offset + toMinutes(p.startTime), end: offset + toMinutes(p.endTime) };
}

/** Monday-first week containing `key`. */
export function getWeekDateKeys(key: string): string[] {
  const dow = (dateFromKey(key).getDay() + 6) % 7;
  const monday = shiftDateKey(key, -dow);
  return Array.from({ length: 7 }, (_, i) => shiftDateKey(monday, i));
}

/** "Tue, Jan 14, 2025". */
export function formatLongDate(key: string): string {
  return dateFromKey(key).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "Jan 9, 2025". */
export function formatContentDate(key: string): string {
  return dateFromKey(key).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function programsFor(programs: Program[], channelId: string, date: string) {
  return programs
    .filter((p) => p.channelId === channelId && p.date === date)
    .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
}

interface Placement {
  channelId: string;
  date: string;
  startMin: number;
  endMin: number;
  /** Program being moved/edited, ignored by the overlap check. */
  ignoreId?: string;
}

/** Error message for an invalid placement, or null when it is allowed.
 * `startMin`/`endMin` are minutes from `date`'s midnight; endMin past 1440
 * means the block runs across midnight into the next day, so the overlap
 * check compares against neighbouring days on one absolute axis. */
export function validatePlacement(programs: Program[], p: Placement): string | null {
  if (p.endMin <= p.startMin) return "End time must be after the start time.";
  if (p.startMin < 0 || p.startMin >= DAY_MINUTES) return "Programs must start within their day.";
  if (p.endMin > MAX_END_MINUTES) return "A program can cross at most one midnight.";
  const clash = programs.find((other) => {
    if (other.channelId !== p.channelId || other.id === p.ignoreId) return false;
    const d = dayDiff(other.date, p.date);
    // Only adjacent days can reach: a same/next-day block, or a previous
    // day's block spilling past midnight into this one.
    if (d < -1 || d > 1) return false;
    const { start, end } = absSpan(other, p.date);
    return p.startMin < end && p.endMin > start;
  });
  return clash ? `This time overlaps "${clash.title}".` : null;
}

/**
 * Clear [startMin, endMin) on a channel/day by cutting what is underneath:
 * fully covered programs are removed, partly covered ones are shortened, and
 * one that spans the whole range is split around it (the tail becomes a new
 * program with an id from `nextId`). The range and the programs may cross
 * midnight, so everything compares on the absolute axis anchored at
 * `range.date`; a tail cut off after midnight is re-homed onto the day it
 * starts. `touchedDates` lists every date whose programs were cut.
 */
export function overwriteRange(
  programs: Program[],
  range: { channelId: string; date: string; startMin: number; endMin: number },
  nextId: () => string,
): { programs: Program[]; affected: number; touchedDates: string[] } {
  const { channelId, date, startMin, endMin } = range;
  let affected = 0;
  const touched = new Set<string>();
  const out: Program[] = [];
  // Write a piece [start, end) of `p` (absolute minutes) back onto the day
  // it starts, keeping minutes relative to that day's own midnight.
  const piece = (p: Program, start: number, end: number): Program => {
    const dayShift = Math.floor(start / DAY_MINUTES);
    return {
      ...p,
      date: shiftDateKey(date, dayShift),
      startTime: toHHmm(start - dayShift * DAY_MINUTES),
      endTime: toHHmm(end - dayShift * DAY_MINUTES),
    };
  };
  for (const p of programs) {
    const d = p.channelId === channelId ? dayDiff(p.date, date) : 99;
    if (d < -1 || d > 1) {
      out.push(p);
      continue;
    }
    const { start: ps, end: pe } = absSpan(p, date);
    if (pe <= startMin || ps >= endMin) {
      out.push(p);
      continue;
    }
    affected += 1;
    touched.add(p.date);
    if (ps < startMin) out.push(piece(p, ps, startMin));
    if (pe > endMin) {
      const tail = piece(p, endMin, pe);
      out.push(ps < startMin ? { ...tail, id: nextId() } : tail);
      touched.add(tail.date);
    }
  }
  return { programs: out, affected, touchedDates: [...touched] };
}

/**
 * Clamp a dragged block against its neighbours so it stops flush instead of
 * overlapping: dragging into the previous clip pins the block at that clip's
 * end, dragging into the next clip pins it so its own end meets that clip's
 * start (back-to-back, no dead air on a 24x7 channel). `rawStart` and the
 * result are absolute minutes on the axis whose zero is `originKey`'s
 * midnight; [0, maxAbs] are the strip walls. When the cursor sits inside an
 * occupied block, the nearer side wins. Returns null when the gap the
 * cursor is in cannot fit the block at all (the caller shows it invalid).
 *
 * With `snapFloorMin` set, the block additionally snaps left to the end of
 * the previous content in its gap (but never left of `snapFloorMin`), so a
 * first drop or a leftward drag lands flush and the stream stays gapless.
 */
export function clampToGap(
  programs: Program[],
  channelId: string,
  originKey: string,
  rawStart: number,
  durationMin: number,
  maxAbs: number,
  ignoreId?: string,
  snapFloorMin?: number,
): number | null {
  const spans = programs
    .filter((p) => p.channelId === channelId && p.id !== ignoreId)
    .map((p) => absSpan(p, originKey))
    .sort((a, b) => a.start - b.start);

  const inside = spans.find((s) => s.start <= rawStart && rawStart < s.end);
  const fitBetween = (floor: number, ceiling: number, want: number): number | null => {
    if (ceiling - floor < durationMin) return null;
    const target = snapFloorMin !== undefined ? Math.max(floor, snapFloorMin) : want;
    return Math.min(Math.max(target, floor), ceiling - durationMin);
  };

  if (inside) {
    const before = spans.filter((s) => s.end <= inside.start);
    const after = spans.filter((s) => s.start >= inside.end);
    const floorLeft = before.length ? Math.max(...before.map((s) => s.end)) : 0;
    const ceilRight = after.length ? Math.min(...after.map((s) => s.start)) : maxAbs;
    // Nearer edge wins; ties fall to the right (after the block).
    return rawStart - inside.start < inside.end - rawStart
      ? fitBetween(floorLeft, inside.start, inside.start - durationMin)
      : fitBetween(inside.end, ceilRight, inside.end);
  }

  const before = spans.filter((s) => s.end <= rawStart);
  const after = spans.filter((s) => s.start > rawStart);
  const floor = before.length ? Math.max(...before.map((s) => s.end)) : 0;
  const ceiling = after.length ? Math.min(...after.map((s) => s.start)) : maxAbs;
  return fitBetween(floor, ceiling, rawStart);
}

/** Earliest free start (minutes) on a channel/day that fits `durationMin`,
 * at or after `from`. The start must be within the day, but the slot may
 * run past midnight when the next day's head is free (validatePlacement
 * arbitrates, including spill-over from the previous day). */
export function findFreeSlot(
  programs: Program[],
  channelId: string,
  date: string,
  durationMin: number,
  from = 0,
): number | null {
  // Candidate starts: `from` itself, then where each occupying block ends
  // (previous-day spill-overs end within this day too).
  const candidates = new Set<number>([snapMinutes(from)]);
  for (const p of programs) {
    if (p.channelId !== channelId) continue;
    const d = dayDiff(p.date, date);
    if (d !== 0 && d !== -1) continue;
    const end = absSpan(p, date).end;
    if (end > from && end < DAY_MINUTES) {
      candidates.add(Math.ceil(end / SNAP_MINUTES) * SNAP_MINUTES);
    }
  }
  for (const start of [...candidates].sort((a, b) => a - b)) {
    if (start < 0 || start >= DAY_MINUTES) continue;
    const placement = { channelId, date, startMin: start, endMin: start + durationMin };
    if (validatePlacement(programs, placement) === null) return start;
  }
  return null;
}

/** Slot length (minutes) for a piece of content: whole 15-minute steps, at least one. */
export function slotMinutesFor(durationSec: number): number {
  return Math.max(SNAP_MINUTES, Math.ceil(durationSec / 60 / SNAP_MINUTES) * SNAP_MINUTES);
}

export type LengthFilter = "all" | "short" | "medium" | "long";
export type SortKey = "newest" | "oldest" | "title" | "longest";

export interface ContentFilters {
  type?: ContentType;
  query: string;
  length: LengthFilter;
  league: string;
  sort: SortKey;
}

export function filterContent(items: ContentItem[], f: ContentFilters): ContentItem[] {
  const q = f.query.trim().toLowerCase();
  const out = items.filter((item) => {
    if (f.type && item.type !== f.type) return false;
    if (f.league !== "all" && item.league !== f.league) return false;
    if (q && !`${item.title} ${item.id}`.toLowerCase().includes(q)) return false;
    const min = item.durationSec / 60;
    if (f.length === "short" && min >= 30) return false;
    if (f.length === "medium" && (min < 30 || min > 120)) return false;
    if (f.length === "long" && min <= 120) return false;
    return true;
  });
  const by: Record<SortKey, (a: ContentItem, b: ContentItem) => number> = {
    newest: (a, b) => b.date.localeCompare(a.date),
    oldest: (a, b) => a.date.localeCompare(b.date),
    title: (a, b) => a.title.localeCompare(b.title),
    longest: (a, b) => b.durationSec - a.durationSec,
  };
  return out.sort(by[f.sort]);
}
